/**
 * "New session" (TRA-1327): staff open a training agent session for the
 * team. The api refuses a second live session and a stack that cannot run
 * one, and its refusal shows under the form.
 */

import { useId, useState } from 'react'

import { getApiErrorDetail } from '@/api/client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useCreateAgentSession } from '@/hooks/useTrainingAgent'

import { parseNewSession } from './newSession'
import type { NewSessionErrors, NewSessionField } from './newSession'

interface NewSessionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (sessionId: string) => void
}

export function NewSessionDialog({ open, onOpenChange, onCreated }: NewSessionDialogProps): React.JSX.Element {
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [errors, setErrors] = useState<NewSessionErrors>({})
  const [refusal, setRefusal] = useState<string | null>(null)
  const create = useCreateAgentSession()
  const ids: Record<NewSessionField, string> = { title: useId(), message: useId() }

  function close(): void {
    setTitle('')
    setMessage('')
    setErrors({})
    setRefusal(null)
    onOpenChange(false)
  }

  function submit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    const parsed = parseNewSession({ title, message })
    setErrors(parsed.kind === 'error' ? parsed.errors : {})
    if (parsed.kind === 'error') return
    setRefusal(null)
    create.mutate(
      { data: parsed.request },
      {
        onSuccess: (response) => {
          close()
          if (response.status === 201) onCreated(response.data.id)
        },
        onError: (error) => {
          setRefusal(getApiErrorDetail(error) ?? 'The session did not start. Try again.')
        },
      },
    )
  }

  const described = (field: NewSessionField): string | undefined =>
    errors[field] === undefined ? undefined : `${ids[field]}-error`

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (create.isPending) return
        if (next) onOpenChange(true)
        else close()
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New session</DialogTitle>
          <DialogDescription>
            The agent reads this team&apos;s data in a sandbox and asks for approval before it runs
            any command.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor={ids.title}>Title</Label>
            <Input
              id={ids.title}
              value={title}
              placeholder="Plan an SFT run on the newest keeper data"
              disabled={create.isPending}
              aria-invalid={errors.title !== undefined}
              aria-describedby={described('title')}
              onChange={(event) => {
                setTitle(event.target.value)
              }}
            />
            <FieldError id={`${ids.title}-error`} message={errors.title} />
          </div>
          <div className="space-y-2">
            <Label htmlFor={ids.message}>First message (optional)</Label>
            <Textarea
              id={ids.message}
              value={message}
              rows={4}
              placeholder="What should the agent look at first?"
              disabled={create.isPending}
              aria-invalid={errors.message !== undefined}
              aria-describedby={described('message')}
              onChange={(event) => {
                setMessage(event.target.value)
              }}
            />
            <FieldError id={`${ids.message}-error`} message={errors.message} />
          </div>
          {refusal !== null && (
            <p role="alert" className="text-sm text-destructive">
              {refusal}
            </p>
          )}
          <DialogFooter className="flex justify-end gap-2">
            <Button type="button" variant="outline" disabled={create.isPending} onClick={close}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Starting…' : 'Start session'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
